// src/components/NotificationBell/NotificationBell.tsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import { getAuthToken } from '../../services/authUtils';
import { useWebSocket } from '../../context/WebSocketContext';
import styles from './NotificationBell.module.css';

interface Alert {
  id: string;
  type: string;
  symbol: string;
  message: string;
  actionUrl: string;
  read: boolean;
  createdAt: string;
}

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { connected, on } = useWebSocket();

  const fetchAlerts = useCallback(async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;
      const res = await fetch(`${API_URL}/api/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setAlerts(data.alerts || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {
      // Silently fail — notifications are non-critical
    }
  }, []);

  // Poll for new notifications — 60s if WebSocket connected, 30s if not
  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, connected ? 60000 : 30000);
    return () => clearInterval(interval);
  }, [fetchAlerts, connected]);

  // Instant notification via WebSocket
  useEffect(() => {
    return on('notification', () => {
      fetchAlerts(); // Refresh from server to get full alert data
    });
  }, [on, fetchAlerts]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    setOpen(!open);
    if (!open) {
      setLoading(true);
      fetchAlerts().finally(() => setLoading(false));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;
      await fetch(`${API_URL}/api/notifications/read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ all: true }),
      });
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  };

  const handleAlertClick = (alert: Alert) => {
    // Mark single alert as read
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;
        await fetch(`${API_URL}/api/notifications/read`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ids: [alert.id] }),
        });
      } catch {
        // Silently fail
      }
    })();

    setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, read: true } : a)));
    setUnreadCount((prev) => Math.max(0, prev - (alert.read ? 0 : 1)));
    setOpen(false);

    if (alert.actionUrl) {
      navigate(alert.actionUrl);
    }
  };

  const formatTime = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  return (
    <div className={styles.wrapper} ref={dropdownRef}>
      <button className={styles.bellBtn} onClick={handleOpen} title="Notifications">
        {'\uD83D\uDD14'}
        {unreadCount > 0 && <span className={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <h3 className={styles.dropdownTitle}>Notifications</h3>
            {unreadCount > 0 && (
              <button className={styles.markAllBtn} onClick={handleMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className={styles.alertList}>
            {loading ? (
              <div className={styles.loading}>Loading...</div>
            ) : alerts.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>{'\uD83D\uDD14'}</div>
                <p>No notifications yet</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`${styles.alertItem} ${!alert.read ? styles.alertUnread : ''}`}
                  onClick={() => handleAlertClick(alert)}
                >
                  <div className={`${styles.alertDot} ${alert.read ? styles.alertDotRead : ''}`} />
                  <div className={styles.alertContent}>
                    {alert.symbol && <div className={styles.alertSymbol}>{alert.symbol}</div>}
                    <p className={styles.alertMessage}>{alert.message}</p>
                    <div className={styles.alertTime}>{formatTime(alert.createdAt)}</div>
                    {alert.actionUrl && <div className={styles.alertAction}>Take action &rarr;</div>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
